import React, { useEffect, useMemo, useState } from "react";
import {
  createSeason,
  createSerializedCollection,
  fetchEpisodesBySeason,
  fetchSeasonsBySeries,
  fetchSerializedCollections,
  uploadEpisode,
} from "../../services/apiCatalogAdmin";
import { buildMediaUrl } from "../../services/streamApi";

const MATURITY_OPTIONS = ["7+", "10+", "12+", "16+", "18+"];

function SerializedUploadPanel({ type, categories, users }) {
  const label = type === "documentaire" ? "documentaire" : "serie";
  const pluralLabel = type === "documentaire" ? "documentaires" : "series";

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [categorieId, setCategorieId] = useState("");
  const [userId, setUserId] = useState("");
  const [maturityRating, setMaturityRating] = useState("13+");
  const [image1, setImage1] = useState(null);
  const [image2, setImage2] = useState(null);
  const [previewImage1, setPreviewImage1] = useState(null);
  const [previewImage2, setPreviewImage2] = useState(null);
  const [existingCollections, setExistingCollections] = useState([]);
  const [selectedCollectionId, setSelectedCollectionId] = useState("");
  const [selectedSeasonId, setSelectedSeasonId] = useState("");
  const [seasonTitle, setSeasonTitle] = useState("");
  const [seasons, setSeasons] = useState([]);
  const [episodesBySeason, setEpisodesBySeason] = useState({});
  const [episodeTitle, setEpisodeTitle] = useState("");
  const [episodeDescription, setEpisodeDescription] = useState("");
  const [episodeNumber, setEpisodeNumber] = useState(1);
  const [episodeFile, setEpisodeFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [progressLabel, setProgressLabel] = useState("");
  const [progressValue, setProgressValue] = useState(0);
  const [message, setMessage] = useState("");

  const selectedCollection = useMemo(
    () => existingCollections.find((collection) => String(collection.id) === String(selectedCollectionId)) || null,
    [existingCollections, selectedCollectionId]
  );

  const refreshCollections = async () => {
    try {
      const data = await fetchSerializedCollections(type);
      setExistingCollections(data);
    } catch (error) {
      console.error("Erreur liste collections:", error);
    }
  };

  const hydrateSeriesStructure = async (collectionId) => {
    if (!collectionId) {
      setSeasons([]);
      setEpisodesBySeason({});
      setSelectedSeasonId("");
      return;
    }

    try {
      const loadedSeasons = await fetchSeasonsBySeries(collectionId);
      setSeasons(loadedSeasons);

      const episodesEntries = await Promise.all(
        loadedSeasons.map(async (season) => [season.id, await fetchEpisodesBySeason(season.id)])
      );

      const nextEpisodesBySeason = Object.fromEntries(episodesEntries);
      setEpisodesBySeason(nextEpisodesBySeason);

      const firstSeasonId = loadedSeasons[0]?.id ? String(loadedSeasons[0].id) : "";
      const resolvedSeasonId =
        selectedSeasonId && nextEpisodesBySeason[selectedSeasonId] ? String(selectedSeasonId) : firstSeasonId;
      setSelectedSeasonId(resolvedSeasonId);
      setSeasonTitle(`Saison ${(loadedSeasons.length || 0) + 1}`);

      const selectedEpisodes = nextEpisodesBySeason[resolvedSeasonId] || [];
      setEpisodeNumber((selectedEpisodes[selectedEpisodes.length - 1]?.episode_number || 0) + 1 || 1);
    } catch (error) {
      console.error("Erreur chargement structure:", error);
    }
  };

  useEffect(() => {
    refreshCollections();
  }, [type]);

  useEffect(() => {
    hydrateSeriesStructure(selectedCollectionId);
  }, [selectedCollectionId]);

  const handleImageSelect = (event, setImage, setPreview) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;
    setImage(selectedFile);
    setPreview(URL.createObjectURL(selectedFile));
  };

  const resetCollectionForm = () => {
    setTitle("");
    setDescription("");
    setCategorieId("");
    setUserId("");
    setImage1(null);
    setImage2(null);
    setPreviewImage1(null);
    setPreviewImage2(null);
    setMaturityRating("13+");
  };

  const handleCreateCollection = async () => {
    if (!title.trim()) {
      alert(`Veuillez renseigner le titre du ${label}.`);
      return;
    }

    setIsSubmitting(true);
    setProgressValue(10);
    setProgressLabel(`Creation de la fiche ${label}...`);
    setMessage("");

    try {
      const formData = new FormData();
      formData.append("type", type);
      formData.append("title", title);
      formData.append("description", description);
      formData.append("categorie_id", categorieId);
      formData.append("user_id", userId);
      formData.append("maturity_rating", maturityRating);
      if (image1) formData.append("video_img_1", image1);
      if (image2) formData.append("video_img_2", image2);

      const response = await createSerializedCollection(formData, (event) => {
        if (event?.total) {
          setProgressValue(Math.max(10, Math.round((event.loaded * 100) / event.total)));
        }
      });

      const contentId = String(response?.data?.contentId || "");
      setProgressValue(100);
      setProgressLabel(`La fiche ${label} a ete creee.`);
      setMessage(`La fiche ${label} a ete creee. Vous pouvez maintenant ajouter les saisons et episodes progressivement.`);

      await refreshCollections();
      resetCollectionForm();
      if (contentId) {
        setSelectedCollectionId(contentId);
      }
    } catch (error) {
      console.error(`Erreur creation ${label}:`, error);
      setMessage(`Erreur lors de la creation du ${label}.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddSeason = async () => {
    if (!selectedCollectionId) {
      alert(`Selectionnez d'abord un ${label} existant.`);
      return;
    }

    if (!seasonTitle.trim()) {
      alert("Veuillez renseigner le titre de la saison.");
      return;
    }

    setIsSubmitting(true);
    setProgressValue(20);
    setProgressLabel(`Ajout de ${seasonTitle}...`);
    setMessage("");

    try {
      await createSeason({
        title: seasonTitle,
        series_id: selectedCollectionId,
        season_number: seasons.length + 1,
      });

      await hydrateSeriesStructure(selectedCollectionId);
      setSeasonTitle(`Saison ${(seasons.length || 0) + 2}`);
      setMessage("Saison ajoutee avec succes.");
      setProgressValue(100);
    } catch (error) {
      console.error("Erreur creation saison:", error);
      setMessage("Erreur lors de l'ajout de la saison.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddEpisode = async () => {
    if (!selectedCollectionId || !selectedSeasonId) {
      alert("Selectionnez une collection et une saison.");
      return;
    }

    if (!episodeTitle.trim() || !episodeFile) {
      alert("Le titre et la video de l'episode sont requis.");
      return;
    }

    setIsSubmitting(true);
    setProgressValue(10);
    setProgressLabel(`Upload de l'episode ${episodeNumber}...`);
    setMessage("");

    try {
      const formData = new FormData();
      formData.append("series_id", selectedCollectionId);
      formData.append("season_id", selectedSeasonId);
      formData.append("title", episodeTitle);
      formData.append("description", episodeDescription);
      formData.append("episode_number", episodeNumber);
      formData.append("maturity_rating", selectedCollection?.maturity_rating || maturityRating);
      formData.append("video", episodeFile);

      await uploadEpisode(selectedCollectionId, selectedSeasonId, formData, (event) => {
        if (event?.total) {
          setProgressValue(Math.max(10, Math.round((event.loaded * 100) / event.total)));
        }
      });

      await hydrateSeriesStructure(selectedCollectionId);
      setEpisodeTitle("");
      setEpisodeDescription("");
      setEpisodeFile(null);
      setEpisodeNumber(Number(episodeNumber) + 1);
      setMessage("Episode ajoute avec succes. Il restera dans le studio jusqu'au transcodage puis a la publication.");
      setProgressValue(100);
    } catch (error) {
      console.error("Erreur ajout episode:", error);
      setMessage("Erreur lors de l'ajout de l'episode.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="upload-page">
      <div className="upload-card">
        <h2 className="upload-subtitle">Ajouter un {label}</h2>
        <p className="upload-helper">
          Cree la fiche principale, puis ajoute les saisons et episodes a ton rythme. Les {pluralLabel} restent dans le studio tant que le transcodage et la publication ne sont pas faits.
        </p>

        <section className="catalog-builder">
          <div className="catalog-builder__header">
            <div>
              <h3>1. Fiche principale</h3>
              <p>Affiche, backdrop, classification parentale et informations de base.</p>
            </div>
          </div>

          <div className="upload-images">
            <div className="upload-image-field">
              <p className="upload-label">Affiche principale</p>
              <div className="upload-box">
                {previewImage1 ? (
                  <img src={previewImage1} alt="Affiche principale" className="upload-thumb" />
                ) : (
                  <div className="upload-placeholder">
                    <span>Affiche principale</span>
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(event) => handleImageSelect(event, setImage1, setPreviewImage1)}
                  className="upload-input"
                />
              </div>
            </div>

            <div className="upload-image-field">
              <p className="upload-label">Backdrop hero</p>
              <div className="upload-box">
                {previewImage2 ? (
                  <img src={previewImage2} alt="Backdrop" className="upload-thumb" />
                ) : (
                  <div className="upload-placeholder">
                    <span>Visuel large Netflix-like</span>
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(event) => handleImageSelect(event, setImage2, setPreviewImage2)}
                  className="upload-input"
                />
              </div>
            </div>
          </div>

          <div className="upload-form">
            <input type="text" placeholder={`Titre du ${label}`} value={title} onChange={(event) => setTitle(event.target.value)} />
            <textarea
              placeholder="Description generale"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              rows={4}
            />

            <div className="upload-grid">
              <select value={categorieId} onChange={(event) => setCategorieId(event.target.value)} disabled={!categories.length}>
                <option value="">{categories.length ? "Selectionner une categorie" : "Aucune categorie"}</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.title}
                  </option>
                ))}
              </select>

              <select value={userId} onChange={(event) => setUserId(event.target.value)} disabled={!users.length}>
                <option value="">{users.length ? "Selectionner un proprietaire" : "Aucun utilisateur"}</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.nom} - {user.phone}
                  </option>
                ))}
              </select>

              <select value={maturityRating} onChange={(event) => setMaturityRating(event.target.value)}>
                {MATURITY_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    Classification {option}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="upload-actions">
            <button type="button" onClick={handleCreateCollection} disabled={isSubmitting}>
              {isSubmitting ? "Creation en cours..." : `Creer la fiche ${label}`}
            </button>
          </div>
        </section>

        <section className="catalog-builder">
          <div className="catalog-builder__header">
            <div>
              <h3>2. Continuer sur un {label} existant</h3>
              <p>Selectionne une fiche deja creee pour lui rattacher de nouvelles saisons ou episodes.</p>
            </div>
            <button type="button" className="catalog-builder__ghost" onClick={refreshCollections}>
              Actualiser
            </button>
          </div>

          <div className="upload-grid">
            <select value={selectedCollectionId} onChange={(event) => setSelectedCollectionId(event.target.value)}>
              <option value="">Choisir un {label}</option>
              {existingCollections.map((collection) => (
                <option key={collection.id} value={collection.id}>
                  {collection.title}
                </option>
              ))}
            </select>

            <select value={selectedSeasonId} onChange={(event) => setSelectedSeasonId(event.target.value)} disabled={!seasons.length}>
              <option value="">{seasons.length ? "Choisir une saison" : "Aucune saison"}</option>
              {seasons.map((season) => (
                <option key={season.id} value={season.id}>
                  {season.title}
                </option>
              ))}
            </select>
          </div>

          {selectedCollection ? (
            <div className="existing-collections__card existing-collections__card--selected">
              <img
                src={buildMediaUrl(selectedCollection.video_img_1 || selectedCollection.video_img_2)}
                alt={selectedCollection.title}
              />
              <div>
                <strong>{selectedCollection.title}</strong>
                <p>{selectedCollection.category_title || selectedCollection.type}</p>
                <p>Classification {selectedCollection.maturity_rating || "a definir"}</p>
              </div>
            </div>
          ) : null}
        </section>

        <section className="catalog-builder">
          <div className="catalog-builder__header">
            <div>
              <h3>3. Ajouter une saison</h3>
              <p>Une fois la fiche creee, tu peux etendre la structure sans tout uploader en une seule fois.</p>
            </div>
          </div>

          <div className="upload-grid">
            <input type="text" value={seasonTitle} onChange={(event) => setSeasonTitle(event.target.value)} placeholder="Saison 1" />
          </div>

          <div className="upload-actions">
            <button type="button" onClick={handleAddSeason} disabled={isSubmitting || !selectedCollectionId}>
              Ajouter la saison
            </button>
          </div>
        </section>

        <section className="catalog-builder">
          <div className="catalog-builder__header">
            <div>
              <h3>4. Ajouter un episode</h3>
              <p>Le backend analysera le fichier pour detecter les pistes audio/sous-titres disponibles avant transcodage.</p>
            </div>
          </div>

          <div className="upload-form">
            <div className="upload-grid">
              <input
                type="number"
                min="1"
                value={episodeNumber}
                onChange={(event) => setEpisodeNumber(event.target.value)}
                placeholder="Numero"
              />
              <input
                type="text"
                value={episodeTitle}
                onChange={(event) => setEpisodeTitle(event.target.value)}
                placeholder="Titre de l'episode"
              />
            </div>

            <textarea
              rows={3}
              value={episodeDescription}
              onChange={(event) => setEpisodeDescription(event.target.value)}
              placeholder="Description de l'episode"
            />

            <label className="episode-builder__file">
              <span>{episodeFile ? episodeFile.name : "Choisir la video de l'episode"}</span>
              <input type="file" accept="video/*" onChange={(event) => setEpisodeFile(event.target.files?.[0] || null)} />
            </label>
          </div>

          <div className="upload-actions">
            <button type="button" onClick={handleAddEpisode} disabled={isSubmitting || !selectedSeasonId}>
              Ajouter l'episode
            </button>
          </div>
        </section>

        {isSubmitting ? (
          <div className="upload-progress upload-progress--large">
            <div className="upload-progress-bar" style={{ width: `${progressValue}%` }} />
            <p>{progressLabel || "Traitement en cours..."}</p>
          </div>
        ) : null}

        {message ? <p className="upload-message">{message}</p> : null}

        <section className="existing-collections">
          <div className="catalog-builder__header">
            <div>
              <h3>{pluralLabel} deja crees</h3>
              <p>Vue rapide des fiches principales, des saisons et du volume d'episodes associes.</p>
            </div>
          </div>

          <div className="existing-collections__grid">
            {existingCollections.map((collection) => (
              <article
                key={collection.id}
                className={`existing-collections__card ${String(collection.id) === String(selectedCollectionId) ? "is-active" : ""}`}
                onClick={() => setSelectedCollectionId(String(collection.id))}
              >
                <img src={buildMediaUrl(collection.video_img_1 || collection.video_img_2)} alt={collection.title} />
                <div>
                  <strong>{collection.title}</strong>
                  <p>{collection.category_title || collection.type}</p>
                </div>
              </article>
            ))}
          </div>

          {selectedCollectionId && seasons.length ? (
            <div className="season-builder">
              {seasons.map((season) => (
                <div key={season.id} className="season-builder__card">
                  <div className="season-builder__header">
                    <div>
                      <h4>{season.title}</h4>
                      <p>{(episodesBySeason[season.id] || []).length} episode(s)</p>
                    </div>
                  </div>

                  <div className="episode-builder">
                    {(episodesBySeason[season.id] || []).map((episode) => (
                      <div key={episode.id} className="episode-builder__card">
                        <div className="episode-builder__grid">
                          <strong>Episode {episode.episode_number}</strong>
                          <span>{episode.title}</span>
                        </div>
                        <p>{episode.description || "Episode ajoute dans le studio."}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </section>
      </div>
    </div>
  );
}

export default SerializedUploadPanel;
